import { modifyTotalOysters } from "../OysterPL8/utils/onShoreFunction";

export const SLATS = "Slats";
const SLATS_PER_PACK = 100;
const APPROX_OYSTER_PER_SLAT = 120;
const GRADE_FALLBACK_COLOURS = [
    "#5b8c5a",
    "#4f7cac",
    "#d17b49",
    "#ad5d6c",
    "#8a6fb3",
    "#c2a23a",
    "#4fa39b",
    "#7c90a0",
];

export const formatOysterMateQuantity = (num, showDoz, alreadyDoz = false) => {
    let newNum = num;
    if (showDoz) {
        newNum = alreadyDoz ? num : num / 12;
    } else {
        newNum = alreadyDoz ? num * 12 : num;
    }
    return Number.isFinite(newNum) ? newNum.toFixed(0) : 0;
};

export const getBatchBackgroundColor = (batchString) => {
    const opacity = 0.6;
    const colours = [
        "rgba(183, 207, 183," + opacity + ")",
        "rgba(199, 219, 218," + opacity + ")",
        "rgba(253, 215, 194," + opacity + ")",
        "rgba(246, 234, 194," + opacity + ")",
        "rgba(181, 234, 214," + opacity + ")",
        "rgba(154, 183, 211," + opacity + ")",
        "rgba(255, 225, 233," + opacity + ")",
    ];
    const match = typeof batchString === "string" ? batchString.match(/[0-9]{4}/) : null;
    if (!match) {
        return "white";
    }
    const year = Number(match[0]);
    return colours[year % colours.length];
};

export const getOysterMateGradeColor = (grade, index = 0) => {
    return grade?.colour || grade?.color || GRADE_FALLBACK_COLOURS[index % GRADE_FALLBACK_COLOURS.length];
};

export const mergeOysterMateGroups = (waterGroups, onShoreGroups) => {
    const merged = Array.isArray(waterGroups) ? [...waterGroups] : [];
    if (!Array.isArray(onShoreGroups)) {
        return merged;
    }

    onShoreGroups.forEach((onShoreGroup) => {
        const foundIndex = merged.findIndex((group) => group.id === onShoreGroup.id);
        if (foundIndex >= 0) {
            merged[foundIndex] = { ...onShoreGroup };
        } else {
            merged.push({ ...onShoreGroup });
        }
    });

    return merged;
};

const getBatchYear = (batchLabel) => {
    if (typeof batchLabel !== "string") {
        return null;
    }

    const match = batchLabel.match(/\d{4}/);
    return match ? Number(match[0]) : null;
};

const compareBatchesByYear = (left, right) => {
    const leftLabel = left?.batch || "";
    const rightLabel = right?.batch || "";
    const leftYear = getBatchYear(leftLabel);
    const rightYear = getBatchYear(rightLabel);

    if (leftYear !== null && rightYear !== null && leftYear !== rightYear) {
        return leftYear - rightYear;
    }

    if (leftYear !== null && rightYear === null) {
        return -1;
    }

    if (leftYear === null && rightYear !== null) {
        return 1;
    }

    return leftLabel.localeCompare(rightLabel);
};

const aggregateGroupData = (items, sortBy) => {
    const grouped = {};

    items.forEach((item) => {
        const baseGroup = item?.cropLabel || "";
        const group1 = sortBy === "By Year" && baseGroup ? baseGroup.substring(0, 4) : baseGroup;
        const group2 = item?.grade?.description || "Unknown";
        let units = Number(item?.totalUnits) || 0;
        let average = Number(item?.average) || 0;

        if (Array.isArray(item?.children) && item.children.length > 0) {
            item.children.forEach((child) => {
                const merged = modifyTotalOysters(
                    { totalUnits: units, average },
                    { totalUnits: child.totalUnits, average: child.average }
                );
                units = Number(merged.totalUnits) || 0;
                average = Number(merged.average) || 0;
            });
        }

        const oysters = units * average;
        const key = `${group1}||${group2}`;
        if (!grouped[key]) {
            grouped[key] = {
                group1,
                group2,
                units: 0,
                oysters: 0,
                colour: item?.grade?.colour || item?.grade?.color || null,
            };
        }

        grouped[key].units += units;
        grouped[key].oysters += oysters;
        if (!grouped[key].colour) {
            grouped[key].colour = item?.grade?.colour || item?.grade?.color || null;
        }
    });

    return Object.values(grouped);
};

export const buildOysterMateTotalsData = (items, sortBy = "By Batch") => {
    const aggregated = aggregateGroupData(items, sortBy);
    const batches = {};

    aggregated.forEach(({ group1, group2, units, oysters, colour }) => {
        const count = group2 === SLATS ? units * SLATS_PER_PACK * APPROX_OYSTER_PER_SLAT : oysters;
        if (!batches[group1]) {
            batches[group1] = { batch: group1, grade: [] };
        }
        batches[group1].grade.push({
            description: group2,
            end: count,
            endUnits: units,
            colour,
        });
    });

    return Object.values(batches)
        .map((batch) => ({
            ...batch,
            totalEnd: batch.grade.reduce((sum, grade) => sum + (Number.isFinite(grade.end) ? grade.end : 0), 0),
        }))
        .sort(compareBatchesByYear);
};